import type { Flow, HomeyAPI, ItemProps } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type FlowProps = ItemProps<Flow>;

type FlowsData = { [key: string]: Flow };
type FlowsById = { [key: string]: FlowProps };

interface State {
  data: FlowsData | null;
  byId: FlowsById | null;
  isLoading: boolean;
  error: unknown;
}

export type FlowsStoreState = State;

export class FlowsStore extends BaseApiStore<State, Flow> {
  constructor() {
    super('flows');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.flow });
      if (abortController.signal.aborted) return;

      const flowsPromise = api.flow.getFlows({ $cache: cache });
      const [flowsData] = await Promise.all([flowsPromise]);

      if (abortController.signal.aborted) return;

      const flows = Object.values(flowsData);

      const data: FlowsData = {};
      const byId: FlowsById = {};

      for (const flowInstance of flows) {
        const flowReference = flowInstance.getProps();

        data[flowReference.id] = flowInstance;
        byId[flowReference.id] = flowReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.flow.on('flow.create', this.handleCreate);
    api.flow.on('flow.update', this.handleUpdate);
    api.flow.on('flow.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.flow.off('flow.create', this.handleCreate);
    api.flow.off('flow.update', this.handleUpdate);
    api.flow.off('flow.delete', this.handleDelete);
  }

  getInstanceFromCache(flow: { id: string }) {
    return this.getCache()[flow.id];
  }

  getCache(): Readonly<FlowsData> {
    const api = this.getApi();

    return api.flow.getCachedItems('flow');
  }

  handleCreate = (createdFlow: Flow) => {
    this.DEBUG && console.info(`create:flow:${createdFlow.id}`);
    const state = this.get();
    const flowInstance = this.getInstanceFromCache(createdFlow);

    if (flowInstance == null) return;

    const flowReference = flowInstance.getProps();

    this.set({
      data: {
        ...state.data,
        [createdFlow.id]: flowInstance,
      },
      byId: {
        ...state.byId,
        [createdFlow.id]: flowReference,
      },
    });
  };

  handleUpdate = (updatedFlow: Flow) => {
    this.DEBUG && console.info(`update:flow:${updatedFlow.id}`);

    this.addToQueue(updatedFlow.id, updatedFlow);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedFlow] of Object.entries(updateQueue)) {
        const flowInstance = this.getInstanceFromCache(queuedFlow);

        if (state.data == null) {
          this.DEBUG && console.info(`update:flow:missing-data`);
          continue;
        }

        if (flowInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:flow:unknown:${id}`);
          continue;
        }

        const flowReference = flowInstance.getProps();

        nextById[id] = flowReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:flow:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedFlow: Pick<Flow, 'id'>) => {
    this.DEBUG && console.info(`delete:flow:${deletedFlow.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:flow:missing-data`);
      return;
    }

    const flowInstance = state.data[deletedFlow.id];

    if (flowInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[flowInstance.id];
    delete nextById[flowInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const flows = Object.values(cache ?? {});
    const data: FlowsData = {};
    const byId: FlowsById = {};

    for (const flowInstance of flows) {
      const flowReference = flowInstance.getProps();
      data[flowReference.id] = flowInstance;
      byId[flowReference.id] = flowReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const flowsStore = new FlowsStore();
